/**
 * "Nächste Ein- und Auszüge" — die Bewegungsübersicht des Co-Living Dashboards
 * (Slice 17).
 *
 * REINE PRÄSENTATION ÜBER EINEN BEREITS GELADENEN RESPONSE. Die Karte macht
 * KEINEN eigenen Fetch. Sie liest `upcoming_events` aus demselben
 * `GET /api/admin/co-living/upcoming`-Snapshot, aus dem `CoLivingWarningsCard`
 * seine `warnings` liest — ein Request, zwei Leser. Der Request-Delta dieses
 * Slices ist 0: `upcoming_events` lag seit Slice 2 auf dem Draht und hatte im
 * Frontend keinen einzigen Consumer.
 *
 * DAS IST KEINE WARNUNGSLISTE. Die Karte stellt Bewegungen dar; ob etwas eine
 * Warnung ist, entscheidet weiterhin allein der Server und zeigt allein
 * `CoLivingWarningsCard`. Insbesondere `tenancy_contract_end` — ein planmässig
 * auslaufender Vertrag ohne Kündigung — ist hier sichtbar, aber ausdrücklich
 * kein Handlungsbedarf (backend docs/KPI_SEMANTICS.md C.6).
 *
 * WELCHE EVENT-TYPEN HIER ERSCHEINEN, ist eine Allowlist, keine Blocklist:
 *
 *   tenancy_move_in       → "Einzug"
 *   tenancy_termination   → "Auszug · Kündigung"
 *   tenancy_contract_end  → "Auszug · Vertragsende"
 *   unit_lease_start      → NICHT hier. Das ist der Beginn des Lease-Fensters
 *                           EINER EINHEIT, kein Ein- oder Auszug eines
 *                           Mieters. Es bleibt im Warnungs-/Event-Contract
 *                           unverändert bedeutsam, nur nicht in dieser Liste.
 *
 * Die Allowlist ist bewusst anders als in `CoLivingWarningsCard`, das einen
 * unbekannten `type` absichtlich trotzdem rendert. Dort ist das richtig: eine
 * Warnung, die der Server schickt und die UI verschweigt, ist ein Fehlermodus.
 * Hier ist die RICHTUNG der Inhalt — ein Event, dessen Typ wir nicht kennen,
 * ist weder ein Einzug noch ein Auszug, und es als eines von beiden zu zeigen
 * wäre eine erfundene Aussage. Unbekannte Typen fallen daher heraus.
 *
 * KEINE KÜNDIGUNGS-/VERTRAGSENDE-DEDUPLIZIERUNG. Der Backend-Emitter schliesst
 * die beiden Auszugstypen gegenseitig aus: `tenancy_termination` entsteht nur
 * über `find_upcoming_terminations`, das
 * `Tenancy.termination_effective_date.isnot(None)` verlangt, und die
 * `tenancy_contract_end`-Schleife beginnt mit
 * `if t.termination_effective_date is not None: continue`. Die beiden
 * Prädikate sind exakte Komplemente auf demselben Feld, also kann dieselbe
 * Tenancy nie beides erzeugen. Ein Frontend-Dedupe wäre toter Code, der eine
 * Contract-Eigenschaft verdeckt.
 *
 * DATE-ONLY BLEIBT DATE-ONLY. `relevant_date` ist im Contract ein `date`, kein
 * Instant. `formatDateOnly` macht für `YYYY-MM-DD` einen reinen String-Split,
 * ohne `new Date()` — damit bleibt 2026-10-01 auch in jeder Geräte-Zeitzone
 * der 01.10.2026 (CLAUDE.md Regel 51 + Regel 33, docs/TIMEZONE_ARCHITECTURE.md).
 *
 * DER HORIZONT KOMMT AUS DEM CONTRACT. Die Karte kennt keine 90 Tage. Sie
 * schneidet nichts ab: welche Events im Fenster liegen, hat der Server bereits
 * entschieden. Sie zeigt die Frist nur an, und zwar aus
 * `horizons.event_days` — fehlt der Wert, nennt sie keine Zahl.
 *
 * Auslagerung aus `AdminCoLivingDashboardPage.js` nach Regel 48: die Page steht
 * bei ~1690 Zeilen und bleibt Orchestrator.
 *
 * SLICE 18 — NUR DIE DARSTELLUNG. Die Zeilen tragen jetzt die Datumskachel der
 * Referenzgrafik und ein Badge statt eines Punktes, und die Karte trägt Tokens
 * statt Hex. Auswahl, Filter, Sortierung, Limit und alle Zustände sind
 * unverändert — `selectUpcomingMoves` ist dieselbe Funktion wie in Slice 17.
 */
import React from "react";

import { formatDateOnly } from "../../../utils/datetime";

/** Kompakte Dashboard-Vorschau, keine vollständige Timeline. */
export const MAX_VISIBLE_MOVES = 5;

/**
 * Label und Ton pro Bewegungstyp. Präsentation, keine Businessregel — welcher
 * Typ existiert und wann er entsteht, entscheidet der Server.
 *
 * `tenancy_termination` ist etwas deutlicher markiert als `tenancy_contract_end`,
 * weil eine erfasste Kündigung der spezifischere bekannte Auszugsgrund ist. Das
 * ist Farbe, keine Dringlichkeitsaussage und keine Warnung.
 */
const MOVE_PRESENTATION = {
  tenancy_move_in: {
    label: "Einzug",
    reason: null,
    badgeClass: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  },
  tenancy_termination: {
    label: "Auszug",
    reason: "Kündigung",
    badgeClass: "bg-destructive/10 text-destructive",
  },
  tenancy_contract_end: {
    label: "Auszug",
    // Planmässiges Vertragsende: neutral getönt, nicht alarmierend. Es ist
    // ausdrücklich kein Handlungsbedarf (KPI_SEMANTICS C.6).
    reason: "Vertragsende",
    badgeClass: "bg-muted text-muted-foreground",
  },
};

/**
 * Die einzige Quelle dafür, was als Bewegung zählt. `unit_lease_start` fehlt
 * hier absichtlich (siehe Datei-Kopf).
 */
const MOVE_TYPES = new Set(Object.keys(MOVE_PRESENTATION));

/**
 * Filter → Sortierung → Limit, in genau dieser Reihenfolge.
 *
 * Die Reihenfolge ist der Vertrag: erst die Population einschränken, dann
 * chronologisch ordnen, erst danach auf `maxCount` kürzen. Andersherum wäre die
 * sichtbare Fünferauswahl nicht die zeitlich nächste, sondern die ersten fünf
 * der Payload — bei mehr als fünf Treffern ein stiller Datenfehler.
 *
 * FAIL CLOSED AUF DER UNIT. `allowedUnitIds` ist die bereits gefilterte
 * Co-Living-Population der Page, als Menge INTERNER Unit-IDs. Ein Event, dessen
 * `unit_id` nicht darin steht, wird nicht gezeigt — und ein Event ohne
 * `unit_id` wird NICHT über `unit_short_id`, `unit_title` oder `room_name`
 * einer Unit zugeordnet. Display-Labels sind keine Join-Wahrheit: zwei Units
 * dürfen dieselbe Anzeige tragen, ihre IDs nie.
 *
 * `null` als `allowedUnitIds` heisst "Filterbasis noch nicht bekannt" und ist
 * NICHT dasselbe wie eine leere Menge. Die Karte behandelt es als Ladezustand,
 * nicht als "nichts steht an".
 *
 * SORTIERUNG IST HIER REDUNDANT, ABER NICHT ÜBERFLÜSSIG. Der Server sortiert
 * `upcoming_events` bereits (`_envelope` → `events.sort(_sort_key)` mit
 * `relevant_date` als erstem Schlüssel), also ist dieser Vergleich gegen den
 * echten Contract ein No-op. Er steht trotzdem hier, weil das Limit oben von
 * einer Ordnung abhängt, die diese Komponente selbst nicht sehen kann.
 * `Array.prototype.sort` ist seit ES2019 stabil, und der Comparator gibt für
 * gleiche Daten 0 zurück — die Backend-Reihenfolge innerhalb eines Tages bleibt
 * damit erhalten. Es wird ausdrücklich KEINE fachliche Priorität erfunden:
 * kein "Einzug vor Auszug", kein "Kündigung vor Vertragsende".
 *
 * Exportiert für Tests — die Pipeline ist die eigentliche Logik dieser Datei.
 *
 * @param {Array<object>|null|undefined} events         `snapshot.upcoming_events`
 * @param {Set<string>|null|undefined}   allowedUnitIds interne Unit-IDs, oder null
 * @param {number}                       maxCount       sichtbares Maximum
 * @returns {Array<object>} die sichtbaren Events, chronologisch
 */
export function selectUpcomingMoves(
  events,
  allowedUnitIds,
  maxCount = MAX_VISIBLE_MOVES
) {
  if (!Array.isArray(events)) return [];
  if (!(allowedUnitIds instanceof Set)) return [];

  const matched = events.filter((event) => {
    if (!event || !MOVE_TYPES.has(event.type)) return false;
    const unitId = event.unit_id;
    if (unitId === null || unitId === undefined || unitId === "") return false;
    return allowedUnitIds.has(String(unitId));
  });

  const ordered = [...matched].sort((a, b) => {
    const left = String(a?.relevant_date ?? "");
    const right = String(b?.relevant_date ?? "");
    if (left === right) return 0;
    return left < right ? -1 : 1;
  });

  return ordered.slice(0, maxCount);
}

/**
 * Anzeigename einer Einheit. Reine Fallback-Kette, keine Herleitung — und
 * bewusst OHNE `unit_id` am Ende: eine rohe UUID ist kein Label (Slice 15).
 * Der Contract liefert `unit_short_id` bereits als kanonische Display-ID, es
 * wird also weder nachgeschlagen noch neu berechnet.
 */
function unitLabel(event) {
  return event.unit_short_id || event.unit_title || null;
}

/** Grammatik, keine Rechnung: `days_until` kommt unverändert vom Server. */
function daysLabel(daysUntil) {
  if (daysUntil === null || daysUntil === undefined) return null;
  return daysUntil === 1 ? "1 Tag" : `${daysUntil} Tage`;
}

/** Stabiler Key ohne Index-Abhängigkeit, wo der Contract eine ID liefert. */
function moveKey(event, index) {
  return [
    event.type ?? "move",
    event.tenancy_id ?? event.unit_id ?? "",
    event.relevant_date ?? "",
    index,
  ].join("-");
}

function MoveMeta({ children }) {
  const parts = React.Children.toArray(children).filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <p className="mt-[3px] text-[10px] leading-[1.5] text-muted-foreground">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <span className="mx-[5px] text-muted-foreground/50">·</span> : null}
          {part}
        </React.Fragment>
      ))}
    </p>
  );
}

/** Kurzform der Monate für die Datumskachel. Eine Beschriftungstabelle, keine
 *  Businessregel — und bewusst hier statt über `Date`, siehe `DateTile`. */
const MONTH_ABBR = [
  "JAN", "FEB", "MÄR", "APR", "MAI", "JUN",
  "JUL", "AUG", "SEP", "OKT", "NOV", "DEZ",
];

/**
 * Die Datumskachel der Referenz: Tag gross, Monat klein.
 *
 * Gelesen wird der `YYYY-MM-DD`-String direkt, mit demselben String-Split, den
 * `formatDateOnly` macht — KEIN `new Date(...)`. Ein Date-Objekt aus einem
 * date-only-Feld verschiebt den Tag in jeder negativen Zeitzone (Regel 51), und
 * genau das würde man einer Kachel, die nur "31 OKT" zeigt, nie ansehen.
 *
 * Die Kachel ist `aria-hidden`: das vollständige Datum steht als Text daneben,
 * und zweimal dasselbe vorzulesen ist Lärm.
 */
function DateTile({ isoDate }) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate ?? ""));
  if (!match) return null;
  const monthIndex = Number(match[2]) - 1;
  return (
    <span
      className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-[8px] bg-muted"
      aria-hidden
    >
      <span className="font-mono text-[15px] font-semibold leading-none text-foreground">
        {match[3]}
      </span>
      <span className="mt-0.5 text-[9px] font-medium tracking-[0.06em] text-muted-foreground">
        {MONTH_ABBR[monthIndex] ?? ""}
      </span>
    </span>
  );
}

function MoveItem({ event }) {
  const presentation = MOVE_PRESENTATION[event.type];
  const date = event.relevant_date ? formatDateOnly(event.relevant_date) : null;
  const days = daysLabel(event.days_until);
  const unit = unitLabel(event);

  return (
    <li className="flex items-start gap-3 border-b border-border py-2.5 last:border-b-0">
      <DateTile isoDate={event.relevant_date} />

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          {/* Kein Ersatzname, wenn der Contract keinen liefert: eine
              Referenznummer ist keine Person, und "Unbekannter Mieter" wäre eine
              Behauptung über Daten, die wir nicht haben. Ohne Namen trägt das
              Datum die Zeile. */}
          <p className="m-0 min-w-0 text-[12px] font-semibold leading-snug text-foreground">
            {event.tenant_display_name || date}
          </p>
          <span
            className={`shrink-0 rounded-[5px] px-1.5 py-0.5 text-[10px] font-semibold ${presentation.badgeClass}`}
          >
            {presentation.label}
          </span>
        </div>

        {/* Datum, Grund, Unit und Raum als Text — der Typ ist nie nur Farbe.
            Ohne `room_name` bleibt die Zeile ohne Raum; die technische
            `room_id` wird nicht ersatzweise sichtbar gemacht. */}
        <MoveMeta>
          {event.tenant_display_name ? date : null}
          {presentation.reason}
          {unit}
          {event.room_name || null}
          {days}
        </MoveMeta>
      </div>
    </li>
  );
}

/**
 * @param {object|null}    snapshot        Antwort von `fetchAdminCoLivingUpcoming`,
 *                                         oder null solange noch geladen wird bzw.
 *                                         nach einem Fehler.
 * @param {string}         error           Fehlermeldung; leer = kein Fehler.
 * @param {Set<string>|null} allowedUnitIds Interne Unit-IDs der aktuell gefilterten
 *                                         Co-Living-Population, oder null solange
 *                                         die Population noch nicht geladen ist.
 */
export default function CoLivingUpcomingMovesCard({
  snapshot,
  error,
  allowedUnitIds,
}) {
  // Drei strikt getrennte Zustände. Ein Fehler darf nie als "nichts steht an"
  // erscheinen, und "Filterbasis noch unbekannt" auch nicht: beides würde eine
  // ruhige Lage behaupten, die niemand gemessen hat.
  const isLoading =
    !error && (snapshot === null || !(allowedUnitIds instanceof Set));

  const visibleMoves = selectUpcomingMoves(
    snapshot?.upcoming_events,
    allowedUnitIds
  );

  // Der Server hat das Fenster bereits angewendet; hier wird es nur benannt.
  // Fehlt die Zahl im Contract, wird keine erfunden.
  const horizonDays = snapshot?.horizons?.event_days;
  const horizonLabel =
    typeof horizonDays === "number" && Number.isFinite(horizonDays)
      ? `Nächste ${horizonDays} Tage`
      : null;

  return (
    // `role="region"` + label: this is a named section of a dense dashboard, so
    // it earns a landmark. It also gives tests one honest handle for "what this
    // card shows", which is what keeps the warnings/moves boundary provable
    // from both sides instead of by page-wide absence.
    <section
      role="region"
      aria-label="Nächste Ein- und Auszüge"
      className="flex flex-col rounded-[10px] border border-border bg-card p-4"
    >
      <h2 className="m-0 text-[13px] font-semibold text-foreground">
        Nächste Ein- und Auszüge
      </h2>
      <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">
        {horizonLabel
          ? `${horizonLabel} · Geplante Bewegungen der gefilterten Units`
          : "Geplante Bewegungen der gefilterten Units"}
      </p>

      <div className="mt-2">
        {isLoading ? (
          <p className="m-0 mt-1 text-[12px] text-muted-foreground">Wird geladen…</p>
        ) : error ? (
          <p className="m-0 mt-1 text-[12px] font-medium text-destructive" role="alert">
            Ein- und Auszüge konnten nicht geladen werden.
          </p>
        ) : visibleMoves.length === 0 ? (
          <p className="m-0 mt-1 text-[12px] text-muted-foreground">
            Keine geplanten Ein- oder Auszüge.
          </p>
        ) : (
          <ul className="m-0 list-none p-0">
            {visibleMoves.map((event, index) => (
              <MoveItem key={moveKey(event, index)} event={event} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
